// Biological age heuristic.
// Sum penalties from known altered biomarkers/algorithms; subtract small bonus
// when overall profile is clean.

import { deriveFlag } from "@/lib/resParser";

const BIO_PENALTIES = [
  // Lipids
  { test: /COLESTEROL TOTAL|COLESTEROL$/i, high: 1.2, low: 0 },
  { test: /LDL/i, high: 1.5, low: 0 },
  { test: /HDL/i, high: 0, low: 1.2 },
  { test: /TRIGLIC|TRIGLYC/i, high: 1.0, low: 0 },
  // Glucose
  { test: /GLUCOS/i, high: 2.0, low: 0.5 },
  { test: /HBA1C|GLIC/i, high: 2.5, low: 0 },
  // Liver
  { test: /GGT/i, high: 1.0, low: 0 },
  { test: /GOT|AST/i, high: 0.8, low: 0 },
  { test: /GPT|ALT/i, high: 0.8, low: 0 },
  // Renal
  { test: /CREATININ/i, high: 0.8, low: 0 },
  { test: /UREA/i, high: 0.6, low: 0 },
  // Minerals & Vitamins
  { test: /VITAMINA D|HIDROXIVITAMIN/i, high: 0, low: 1.2 },
  { test: /VITAMINA B12|B12/i, high: 0, low: 0.6 },
  { test: /MAGNES/i, high: 0, low: 0.5 },
  { test: /ZINC/i, high: 0, low: 0.5 },
  { test: /HIERRO|FERRITIN/i, high: 0, low: 0.6 },
  // Inflammation / other
  { test: /HEMOGLOB|HEMATIES/i, high: 0, low: 0.6 },
  { test: /TSH/i, high: 0.8, low: 0.8 },
];

export function estimateBiologicalAge({ chronologicalAge, biomarkers = [] }) {
  if (chronologicalAge == null || Number.isNaN(chronologicalAge)) return null;
  if (chronologicalAge < 15 || chronologicalAge > 100) return null;

  let penalty = 0;
  const drivers = [];

  for (const b of biomarkers) {
    const flag = deriveFlag(b);
    if (flag === "normal") continue;
    for (const rule of BIO_PENALTIES) {
      if (!rule.test.test(b.name)) continue;
      const p = flag === "high" ? rule.high : flag === "low" ? rule.low : 0;
      if (p > 0) {
        penalty += p;
        drivers.push({ name: b.name, direction: flag, weight: p });
      }
      break;
    }
  }

  // Small bonus when ratio of in-range is excellent
  const total = biomarkers.length || 1;
  const inRange = biomarkers.filter((b) => b.isNormal).length;
  const ratio = inRange / total;
  const bonus = ratio > 0.9 ? -2 : ratio > 0.8 ? -1 : 0;

  const raw = chronologicalAge + penalty + bonus;
  // Clamp to reasonable window
  const bioAge = Math.max(
    chronologicalAge - 8,
    Math.min(chronologicalAge + 14, raw)
  );

  const delta = Math.round((bioAge - chronologicalAge) * 10) / 10;

  return {
    biologicalAge: Math.round(bioAge * 10) / 10,
    chronologicalAge,
    delta,
    drivers: drivers.sort((a, b) => b.weight - a.weight).slice(0, 5),
    ratioInRange: ratio,
  };
}
